import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryProvider } from '@/providers/QueryProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { VenueProvider } from '@/providers/VenueProvider';
import { Toaster } from 'sonner';
import '@/lib/i18n';

import AuthLayout from '@/layouts/AuthLayout';
import CustomerLayout from '@/layouts/CustomerLayout';
import AdminLayout from '@/layouts/AdminLayout';
import PlatformAdminLayout from '@/layouts/PlatformAdminLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
import PlatformAdminGuard from '@/components/guards/PlatformAdminGuard';

import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import MyBookingsPage from '@/pages/customer/MyBookingsPage';
import HomePage from '@/pages/customer/HomePage';
import AvailabilityPage from '@/pages/customer/AvailabilityPage';
import CheckoutPage from '@/pages/customer/CheckoutPage';
import ConfirmationPage from '@/pages/customer/ConfirmationPage';
import UnlockPage from '@/pages/customer/UnlockPage';
import BookingDetailPage from '@/pages/customer/BookingDetailPage';
import SessionPage from '@/pages/customer/SessionPage';
import CreditsPage from '@/pages/customer/CreditsPage';
import AccountPage from '@/pages/customer/AccountPage';
import MarketingHomePage from '@/pages/marketing/MarketingHomePage';
import BookDemoPage from '@/pages/marketing/BookDemoPage';
import PrivacyPolicyPage from '@/pages/marketing/PrivacyPolicyPage';
import VenuesIndexPage from '@/pages/venues/VenuesIndexPage';
import VenueDetailPage from '@/pages/venues/VenueDetailPage';
import DashboardPage from '@/pages/admin/DashboardPage';
import BookingsPage from '@/pages/admin/BookingsPage';
import UnitsPage from '@/pages/admin/UnitsPage';
import UnitDetailPage from '@/pages/admin/UnitDetailPage';
import AddonsPage from '@/pages/admin/AddonsPage';
import AlertsPage from '@/pages/admin/AlertsPage';
import IncidentsPage from '@/pages/admin/IncidentsPage';
import PricingPage from '@/pages/admin/PricingPage';
import ReportsPage from '@/pages/admin/ReportsPage';
import SettingsPage from '@/pages/admin/SettingsPage';
import CustomersPage from '@/pages/admin/CustomersPage';
import PlatformTenantsPage from '@/pages/platform/PlatformTenantsPage';
import PlatformTenantDetailPage from '@/pages/platform/PlatformTenantDetailPage';
import PlatformOnboardingPage from '@/pages/platform/PlatformOnboardingPage';
import PlatformConnectPage from '@/pages/platform/PlatformConnectPage';
import PlatformAlertsPage from '@/pages/platform/PlatformAlertsPage';
import PlatformReportsPage from '@/pages/platform/PlatformReportsPage';
import PlatformSettingsPage from '@/pages/platform/PlatformSettingsPage';
import SetPasswordPage from '@/pages/auth/SetPasswordPage';
import OutageDecisionPage from '@/pages/customer/OutageDecisionPage';

import { DEFAULT_VENUE_SLUG } from '@/lib/constants';

/**
 * Backward-compat redirect: old `/book/:sportCode(/checkout)?` URLs send
 * customers to the new `/venue/{DEFAULT_VENUE_SLUG}/book/...` path. Keeps
 * any bookmarks or outbound links working through the URL restructure.
 */
function RedirectLegacyBook({ to }: { to: 'root' | 'sport' | 'checkout' }) {
  const { sportCode } = useParams<{ sportCode: string }>();
  const base = `/venue/${DEFAULT_VENUE_SLUG}/book`;
  if (to === 'root') return <Navigate to={base} replace />;
  if (to === 'sport') return <Navigate to={`${base}/${sportCode}`} replace />;
  return <Navigate to={`${base}/${sportCode}/checkout`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <QueryProvider>
        <AuthProvider>
          <VenueProvider>
          <Routes>
            {/* Auth routes (public) */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* Standalone pages — no layout */}
            {/* Operator invite redemption — public, the token IS the auth. */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Outage decision (§11.13 Layer 2) — public, the CTA token IS the auth. */}
            <Route path="/outage-decision" element={<OutageDecisionPage />} />
            <Route path="/unlock/:code" element={<UnlockPage />} />
            <Route path="/" element={<MarketingHomePage />} />
            <Route path="/demo" element={<BookDemoPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            {/* Venue discovery surfaces render their own navbar (MarketingNavbar / VenueNavbar) — keep them out of CustomerLayout. */}
            <Route path="/venues" element={<VenuesIndexPage />} />
            <Route path="/venue/:slug" element={<VenueDetailPage />} />

            {/* Booking flow + authenticated customer surfaces (shared chrome) */}
            <Route element={<CustomerLayout />}>
              {/* Booking flow, now nested under /venue/:slug/book/* */}
              <Route path="/venue/:slug/book" element={<HomePage />} />
              <Route path="/venue/:slug/book/:sportCode" element={<AvailabilityPage />} />
              <Route
                path="/venue/:slug/book/:sportCode/checkout"
                element={<CheckoutPage />}
              />

              {/* Protected customer routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/booking/:id/confirmed" element={<ConfirmationPage />} />
                <Route path="/bookings" element={<MyBookingsPage />} />
                <Route path="/bookings/:id" element={<BookingDetailPage />} />
                <Route path="/session/:id" element={<SessionPage />} />
                <Route path="/credits" element={<CreditsPage />} />
                <Route path="/account" element={<AccountPage />} />
              </Route>
            </Route>

            {/* Backward-compat redirects for the legacy /book/* URL scheme */}
            <Route path="/book" element={<RedirectLegacyBook to="root" />} />
            <Route path="/book/:sportCode" element={<RedirectLegacyBook to="sport" />} />
            <Route
              path="/book/:sportCode/checkout"
              element={<RedirectLegacyBook to="checkout" />}
            />
            <Route
              path="/checkout"
              element={<Navigate to={`/venue/${DEFAULT_VENUE_SLUG}/book`} replace />}
            />

            {/* Admin routes (protected, operator roles) */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['OPERATOR_ADMIN', 'OPERATOR_STAFF', 'PLATFORM_ADMIN']}
                />
              }
            >
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<DashboardPage />} />
                <Route path="/admin/bookings" element={<BookingsPage />} />
                <Route path="/admin/customers" element={<CustomersPage />} />
                <Route path="/admin/units" element={<UnitsPage />} />
                <Route path="/admin/units/:id" element={<UnitDetailPage />} />
                <Route path="/admin/addons" element={<AddonsPage />} />
                <Route path="/admin/alerts" element={<AlertsPage />} />
                <Route path="/admin/incidents" element={<IncidentsPage />} />
                <Route path="/admin/pricing" element={<PricingPage />} />
                <Route path="/admin/reports" element={<ReportsPage />} />
                <Route path="/admin/settings" element={<SettingsPage />} />
              </Route>
            </Route>

            {/* Platform admin (PLATFORM_ADMIN only) — distinct guard so
                operators get redirected to /admin, not to /. */}
            <Route element={<PlatformAdminGuard />}>
              <Route element={<PlatformAdminLayout />}>
                <Route path="/platform" element={<PlatformTenantsPage />} />
                <Route path="/platform/tenants/:id" element={<PlatformTenantDetailPage />} />
                <Route path="/platform/onboarding" element={<PlatformOnboardingPage />} />
                <Route path="/platform/connect" element={<PlatformConnectPage />} />
                <Route path="/platform/alerts" element={<PlatformAlertsPage />} />
                <Route path="/platform/reports" element={<PlatformReportsPage />} />
                <Route path="/platform/settings" element={<PlatformSettingsPage />} />
              </Route>
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
          </VenueProvider>
        </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}
